'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Eye, TrendingUp } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useBrandLogos } from '@/hooks/useBrandLogos';
import { createClient } from '@/lib/supabase/client';

// Program dimensions: 5.75 x 8.75 inches → ratio 0.657
// At display width 192px → height = 192 / 0.657 ≈ 292px
// Header = 20% of 292 ≈ 58px | Content = 80% ≈ 234px
const PROG_W = 192;
const PROG_H = Math.round(PROG_W / (5.75 / 8.75)); // ≈ 292
const HEADER_H = Math.round(PROG_H * 0.2); // ≈ 58
const CONTENT_H = PROG_H - HEADER_H; // ≈ 234

const programs = [
  {
    id: 'prog-001',
    title: 'The Phantom of the Opera',
    slug: 'phantom-of-the-opera',
    school: 'Summit Arts Center',
    city: 'Denver, CO',
    views: 4120,
    badge: 'Trending',
    badgeColor: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    accentColor: 'rgba(244,63,94,0.7)',
    headerAccent: '#f43f5e',
  },
  {
    id: 'prog-002',
    title: 'Hamilton',
    slug: 'hamilton',
    school: 'Westside High School',
    city: 'Austin, TX',
    views: 3240,
    badge: 'Most Viewed',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    accentColor: 'rgba(245,158,11,0.7)',
    headerAccent: '#f59e0b',
  },
  {
    id: 'prog-003',
    title: 'Chicago',
    slug: 'chicago',
    school: 'Lakewood Performing Arts',
    city: 'Seattle, WA',
    views: 2750,
    badge: 'Staff Pick',
    badgeColor: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    accentColor: 'rgba(139,92,246,0.7)',
    headerAccent: '#8b5cf6',
  },
  {
    id: 'prog-004',
    title: 'Les Misérables',
    slug: 'les-miserables',
    school: 'Northbrook Academy',
    city: 'Chicago, IL',
    views: 2180,
    badge: 'New',
    badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    accentColor: 'rgba(59,130,246,0.7)',
    headerAccent: '#3b82f6',
  },
  {
    id: 'prog-005',
    title: 'Grease',
    slug: 'grease',
    school: 'Riverside Theatre Co.',
    city: 'Nashville, TN',
    views: 1890,
    badge: 'Popular',
    badgeColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    accentColor: 'rgba(16,185,129,0.7)',
    headerAccent: '#10b981',
  },
  {
    id: 'prog-006',
    title: 'Into the Woods',
    slug: 'into-the-woods',
    school: 'Crestview High School',
    city: 'Portland, OR',
    views: 1640,
    badge: 'Rising',
    badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    accentColor: 'rgba(6,182,212,0.7)',
    headerAccent: '#06b6d4',
  },
];

export default function PopularPrograms() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({ threshold: 0.05 });
  const { logoWordmarkUrl } = useBrandLogos();
  const [coverImages, setCoverImages] = useState<Record<string, string | null>>({});

  useEffect(() => {
    async function fetchCovers() {
      const supabase = createClient();
      const slugs = programs.map((p) => p.slug);
      const { data } = await supabase
        .from('musicals')
        .select('slug, cover_image_url')
        .in('slug', slugs);
      if (data) {
        const map: Record<string, string | null> = {};
        data.forEach((row: { slug: string; cover_image_url: string | null }) => {
          map[row.slug] = row.cover_image_url;
        });
        setCoverImages(map);
      }
    }
    fetchCovers();
  }, []);

  return (
    <section id="programs" className="py-24" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(30,34,53,0.6) 100%)' }}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div
          ref={headerRef}
          className={`flex items-center justify-between mb-10 ${headerVisible ? 'reveal-up delay-0' : 'reveal-hidden'}`}
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={18} className="text-gold-400" />
              <span className="text-xs font-bold text-gold-400 tracking-widest uppercase">Most Popular</span>
            </div>
            <h2 className="text-3xl font-extrabold text-zinc-100">Trending Programs</h2>
          </div>
          <Link
            href="/sign-up-login-screen"
            className="hidden sm:flex items-center gap-2 text-sm font-semibold text-gold-400 hover:text-gold-300 transition-colors"
          >
            Browse all programs →
          </Link>
        </div>

        <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 justify-items-center">
          {programs?.map((prog, idx) => {
            const coverUrl = coverImages[prog.slug];
            return (
              <Link
                key={prog?.id}
                href="/program-viewer"
                className={`group hover:scale-[1.05] hover:-translate-y-1.5 transition-all duration-250 ease-out hover:shadow-[0_16px_40px_rgba(0,0,0,0.35)] relative overflow-hidden ${gridVisible ? 'reveal-up' : 'reveal-hidden'}`}
                style={{
                  width: PROG_W,
                  height: PROG_H,
                  border: `1px solid ${prog?.accentColor}`,
                  borderRadius: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  animationDelay: gridVisible ? `${idx * 70}ms` : '0ms',
                }}
              >
                {/* Gold accent line on left edge */}
                <div className="absolute top-0 left-0 w-0.5 h-full z-10 transition-all duration-250 group-hover:w-1" style={{ background: `linear-gradient(180deg, ${prog?.headerAccent}, ${prog?.accentColor})` }} />

                {/* 20% black header */}
                <div
                  className="flex flex-col items-center justify-center px-4 shrink-0"
                  style={{ height: HEADER_H, backgroundColor: '#09090b' }}
                >
                  {logoWordmarkUrl ? (
                    <img
                      src={logoWordmarkUrl}
                      alt="Ovation"
                      className="object-contain"
                      style={{ height: '14px', filter: `brightness(0) saturate(100%) invert(1) sepia(1) saturate(2) hue-rotate(0deg)`, maxWidth: '80%' }}
                    />
                  ) : (
                    <span className="font-bold tracking-[0.15em] uppercase" style={{ fontSize: 8, color: prog?.headerAccent }}>Program</span>
                  )}
                  <div className="text-white/60 leading-tight text-center mt-0.5" style={{ fontSize: 6 }}>{prog?.school}</div>
                  <div className="text-white/40 leading-tight text-center" style={{ fontSize: 6 }}>{prog?.city}</div>
                </div>

                {/* 80% content area */}
                {coverUrl ? (
                  <div
                    className="relative overflow-hidden"
                    style={{ height: CONTENT_H }}
                  >
                    <img
                      src={coverUrl}
                      alt={`${prog.title} cover`}
                      className="w-full h-full object-cover"
                    />
                    {/* Badge overlay */}
                    <div className="absolute top-2 right-2">
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 border ${prog?.badgeColor}`}
                        style={{ borderRadius: 2 }}
                      >
                        {prog?.badge}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex flex-col items-center justify-between p-3 bg-white"
                    style={{ height: CONTENT_H }}
                  >
                    {/* Badge */}
                    <div className="w-full flex justify-end">
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 border ${prog?.badgeColor}`}
                        style={{ borderRadius: 2 }}
                      >
                        {prog?.badge}
                      </span>
                    </div>

                    {/* Title block */}
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-1">
                      <div className="text-[8px] text-zinc-400 tracking-widest uppercase font-sans mb-1">Musical Program</div>
                      <div className="text-[11px] font-bold text-zinc-900 font-sans leading-tight">{prog?.title}</div>
                      <div className="mt-2 w-8 h-px" style={{ backgroundColor: prog?.headerAccent }} />
                    </div>

                    {/* School / views */}
                    <div className="w-full">
                      <div className="flex items-center gap-1 text-[8px] text-zinc-400">
                        <Eye size={9} />
                        <span className="tabular-nums">{prog?.views?.toLocaleString()}</span>
                        <span>views</span>
                      </div>
                    </div>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}